import { ApiError, apiBaseUrl, apiRequest } from './api';
import { onRecordingStateChange } from './recordingState';
import { closeAllLiveSources, noticeAllLiveSources, openLiveStreamCount } from './deepgramLive';
import { t } from './language';

/**
 * Canlı kayıt sinyali: kayıt sürerken sunucuya dakikada bir "hâlâ kayıttayım"
 * ve açık Deepgram bağlantı sayısı gidiyor (web `/api/v1/live-sessions`).
 * Yönetim konsolu "şu an kayıtta" listesini ve Deepgram canlı döküm
 * maliyetini buradan hesaplıyor; süre sunucu saatiyle ölçülüyor.
 *
 * Kaydı asla etkilemez: sunucuya ulaşılamazsa sessizce vazgeçilir, bir
 * sonraki sinyalde yeniden denenir. Aylık kota bu oturumların süresinden
 * ölçülüyor; sunucu döküm anahtarını yalnızca açık oturum varken veriyor
 * (bkz. ensureLiveSession) ve kota dolunca canlı dökümü kapattırıyor.
 */

const PING_MS = 60_000;

let sessionId: string | null = null;
let timer: NodeJS.Timeout | null = null;
let starting: Promise<void> | null = null;
let recording = false;
/** Sunucu oturumu "başka yerde kayıt sürüyor" diye reddetti (aynı anda tek kayıt). */
let refused: ApiError | null = null;
/** Bu kayıtta kota takviyesi bildirildi mi (bir kez gösterilir). */
let graceNotified = false;

async function start(): Promise<void> {
  refused = null;
  try {
    const { id } = await apiRequest<{ id: string }>('POST', '/api/v1/live-sessions', { body: { action: 'start' }, timeoutMs: 10_000 });
    sessionId = id;
  } catch (err) {
    if (err instanceof ApiError && err.reason === 'concurrent_recording') refused = err;
    console.warn('[liveSessions] oturum açılamadı:', (err as Error).message);
  }
}

/**
 * Döküm anahtarı istenmeden önce çağrılır: sunucu anahtarı yalnızca açık bir
 * canlı oturum varken veriyor. Oturumun kimliğini döndürür (anahtar isteğine
 * eklenir); açılamadıysa null. Hesapta başka bir kayıt sürdüğü için
 * reddedildiyse sunucunun mesajıyla hata fırlatır (canlı döküm açılmaz).
 */
export async function ensureLiveSession(): Promise<string | null> {
  if (sessionId) return sessionId;
  if (!apiBaseUrl()) return null;
  await (starting ??= start().finally(() => (starting = null)));
  if (!sessionId && refused) throw refused;
  return sessionId;
}

async function ping(end: boolean): Promise<void> {
  if (!sessionId) {
    if (end || !recording) return;
    await (starting ??= start().finally(() => (starting = null)));
    return;
  }
  const id = sessionId;
  try {
    const result = await apiRequest<{ ok: boolean; overQuota?: boolean; quotaGrace?: boolean }>('POST', '/api/v1/live-sessions', {
      body: { action: end ? 'end' : 'ping', id, streams: openLiveStreamCount() },
      timeoutMs: 10_000,
    });
    // Kayıt sürerken kota doldu: canlı döküm kapanır, ses kaydı sürer.
    if (!end && result?.overQuota && openLiveStreamCount() > 0) {
      await closeAllLiveSources(
        t(
          'Bu dönemki kayıt kotan doldu; canlı döküm durduruldu. Ses kaydı devam ediyor.',
          'Your monthly recording quota is used up; live transcription stopped. Audio recording continues.'
        )
      );
    } else if (!end && result?.quotaGrace && !graceNotified && openLiveStreamCount() > 0) {
      // Kota bu kayıt sürerken doldu: toplantı yarıda kesilmesin diye takviye verildi.
      graceNotified = true;
      noticeAllLiveSources(
        t(
          'Bu dönemki kayıt kotan doldu. Toplantını bitirebilmen için en fazla 60 dakika ek süre tanındı.',
          'Your monthly recording quota is used up. You have up to 60 extra minutes to finish this meeting.'
        )
      );
    }
  } catch (err) {
    // Sunucu oturumu kapatmış (uzun kopma): sonraki sinyalde yenisi açılır.
    if (err instanceof ApiError && err.reason === 'session_closed') sessionId = null;
    else console.warn('[liveSessions] sinyal gönderilemedi:', (err as Error).message);
  }
  if (end) sessionId = null;
}

function onChange(active: boolean): void {
  recording = active;
  if (active) {
    graceNotified = false;
    if (timer) clearInterval(timer);
    // İlk sinyal bağlantılar açıldıktan sonra; oturum hemen açılır (döküm
    // bağlantısı ensureLiveSession ile önce açtıysa ikincisi açılmaz).
    if (!sessionId) starting ??= start().finally(() => (starting = null));
    timer = setInterval(() => void ping(false), PING_MS);
  } else {
    if (timer) clearInterval(timer);
    timer = null;
    void (starting ?? Promise.resolve()).then(() => ping(true));
  }
}

export function startLiveSessionSignals(): void {
  if (!apiBaseUrl()) return;
  onRecordingStateChange(onChange);
}

/** Uygulama kapanırken açık oturumu kapat (beklemeden). */
export function endLiveSessionSignal(): void {
  if (recording) onChange(false);
}
